import { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Backdrop, Fade } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PremiumAccessService from '../../services/PremiumAccessService';
import authService from '../../services/AuthService';
import { tokens } from '../../theme';
import { useTheme } from '@mui/material';
import PurchasePremiumModal from './PurchasePremiumModal';

// Import the PREMIUM_FEATURES_ENABLED constant
const { PREMIUM_FEATURES_ENABLED } = PremiumAccessService;

/**
 * PremiumContent component
 * Wraps premium content and shows a locked overlay if the user doesn't have access
 */
const PremiumContent = ({ 
  featureId, 
  children, 
  height = "100%", 
  width = "100%", 
  onAccessChange = null,
  blurIntensity = 10,
  showLoginButton = true
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);

  // Check if user is authenticated and has access to this feature
  useEffect(() => {
    const checkAccess = async () => {
      setLoading(true);
      setError(null);
      try {
        // Check if premium features are globally enabled
        if (!PREMIUM_FEATURES_ENABLED) {
          // If premium features are disabled, provide free access to everyone
          setAccessStatus({
            hasAccess: true,
            reason: 'premium_disabled',
            message: 'Premium features are currently freely available to all users'
          });
          if (onAccessChange) onAccessChange({hasAccess: true, reason: 'premium_disabled'});
          setLoading(false);
          return;
        }
        
        // Get user information
        const authData = authService.getCurrentUser();
        const username = authData?.username;
        setIsLoggedIn(!!username);
        
        if (!username) {
          setAccessStatus({
            hasAccess: false,
            reason: 'not_logged_in',
            subscriptionPrice: PremiumAccessService.SUBSCRIPTION_PRICE,
            subscriptionDuration: PremiumAccessService.SUBSCRIPTION_DURATION_DAYS,
            message: 'Login required to access premium features.'
          });
          setLoading(false);
          return;
        }
        
        // Use the new PremiumAccessService to check access based on subscription
        const accessResult = await PremiumAccessService.checkPremiumAccess(featureId);
        
        setAccessStatus(accessResult);
        if (onAccessChange) onAccessChange(accessResult);
        
      } catch (error) {
        console.error('Error checking premium access:', error);
        setError(error.message || "Error verifying access");
        setAccessStatus({
          hasAccess: false,
          reason: 'error',
          message: 'Could not verify access status. Please try again later.'
        });
        
        // Retry logic with exponential backoff (max 3 retries)
        if (retryCount < 3) {
          const timeout = Math.pow(2, retryCount) * 1000;
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, timeout);
        }
      } finally {
        setLoading(false);
      }
    };

    // Subscribe to auth changes
    const unsubscribe = authService.subscribe(({ isLoggedIn, currentUser }) => {
      setIsLoggedIn(isLoggedIn);
      
      // Re-check access when auth status changes
      if (isLoggedIn) {
        checkAccess();
      } else {
        // If premium features are disabled globally, provide free access even to logged out users
        if (!PREMIUM_FEATURES_ENABLED) {
          setAccessStatus({
            hasAccess: true,
            reason: 'premium_disabled',
            message: 'Premium features are currently freely available to all users'
          });
          setLoading(false);
        } else {
          setAccessStatus({
            hasAccess: false,
            reason: 'not_logged_in',
            message: 'Login required to access premium features.'
          });
          setLoading(false);
        }
      }
    });

    // Clean up
    return () => unsubscribe();
  }, [featureId, onAccessChange, retryCount]);

  const handleLogin = () => {
    // Dispatch event to trigger login modal
    document.dispatchEvent(new Event('openLoginModal'));
  };

  const handleSubscribe = () => {
    // Open purchase modal
    setPurchaseModalOpen(true);
  };
  
  const handlePurchaseSuccess = () => {
    // Close modal and force re-check access
    setPurchaseModalOpen(false);
    setRetryCount(prev => prev + 1);
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  // If loading, show spinner
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height={height}
        width={width}
        sx={{
          backgroundColor: colors.primary[400],
          borderRadius: "8px"
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  // Default content rendering
  return (
    <Box 
      position="relative"
      height={height}
      width={width}
      borderRadius="8px"
      overflow="hidden"
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
      }}
    >
      {/* Actual content */}
      <Box 
        sx={{ 
          filter: !accessStatus?.hasAccess ? `blur(${blurIntensity}px)` : 'none',
          height: '100%',
          width: '100%',
          transition: 'filter 0.3s ease-in-out',
          pointerEvents: !accessStatus?.hasAccess ? 'none' : 'auto',
          flex: 1,
        }}
      >
        {children}
      </Box>

      {/* Lock overlay */}
      {!accessStatus?.hasAccess && (
        <Fade in={!accessStatus?.hasAccess}>
          <Backdrop
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 9,
              display: 'flex',
              flexDirection: 'column',
              padding: 3,
              color: colors.grey[100],
              borderRadius: "8px",
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              height: '100%',
              width: '100%',
            }}
            open={!accessStatus?.hasAccess}
          >
            <LockIcon sx={{ fontSize: 40, color: colors.redAccent[500], mb: 2 }} />
            
            <Typography variant="h5" fontWeight={600} color={colors.grey[100]} gutterBottom>
              {accessStatus?.feature?.name || 'Premium Feature'}
            </Typography>
            
            {error ? (
              // If error occurred, show error info and retry button
              <>
                <Typography variant="body1" align="center" paragraph color={colors.redAccent[300]}>
                  {accessStatus?.message || 'Error verifying premium access.'}
                </Typography>
                
                <Button
                  variant="outlined"
                  color="info"
                  onClick={handleRetry}
                  sx={{ mt: 2 }}
                >
                  Retry Verification
                </Button>
              </>
            ) : (
              // Normal display without error
              <>
                <Typography variant="body1" align="center" paragraph>
                  {accessStatus?.message || 'This content requires premium access.'}
                </Typography>
                
                {accessStatus?.reason === 'no_subscription' && (
                  <>
                    <Typography variant="body2" color={colors.greenAccent[400]} align="center" paragraph>
                      Subscribe for {accessStatus.subscriptionPrice || 1000} DEC to get 30 days of premium access.
                    </Typography>
                  </>
                )}

                {!isLoggedIn && showLoginButton ? (
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<LockOpenIcon />}
                    onClick={handleLogin}
                    sx={{ mt: 2 }}
                  >
                    Login to Access
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<LockOpenIcon />}
                    onClick={handleSubscribe}
                    sx={{ mt: 2 }}
                  >
                    Subscribe Now
                  </Button>
                )}
              </>
            )}
          </Backdrop>
        </Fade>
      )}
      
      {/* Purchase Premium Modal */}
      <PurchasePremiumModal
        open={purchaseModalOpen}
        onClose={() => setPurchaseModalOpen(false)}
        onSuccess={handlePurchaseSuccess}
      />

    </Box>
  );
};

export default PremiumContent;